/* Suggested path: frontend/src/components/SkillLoadingScreen.jsx
   -----------------------------------------------------------------
   Shared loading screen for exactly three pages: Dashboard, Skills
   and Precision Storytelling.

   NOT used by AI Prompting, Sales & Negotiation or Copywriting. Those
   three keep their own bespoke loaders (SkeletonLoader in
   ai-prompting/components/Term.jsx, SalesSkeleton inside
   SalesNegotiationLanding.jsx, CopywritingSkeleton inside
   CopywritingLanding.jsx), each tuned to its own skill's material.
   An earlier pass folded them in here; that was wrong and has been
   reverted. The `theme` forcing that existed only to serve those two
   hardcoded pages went with it, so this component simply follows the
   app theme.

   ── ICONS ──
     iconType="logo"  ->  /logo.svg, the navbar/sidebar brand mark. One
                          theme-agnostic SVG with a hardcoded #ea580c
                          fill and NO day/night variants, so dark mode
                          is a filter, matching Navbar/Sidebar.
     iconType="art"   ->  a day/night image pair, same convention as the
                          storytelling castle, character and spool art:
                          BOTH <img> tags always render and CSS picks.

                          By default this is built as a FILE PATH,
                          <iconDir>/<iconSlug>-day.png / -night.png, and
                          fetched over the network like any other image.

                          Pass `daySrc`/`nightSrc` directly to bypass
                          that and use whatever the caller hands in —
                          in particular a base64 data: URI, which ships
                          as part of the JS bundle instead of a separate
                          request. Precision Storytelling uses this: see
                          features/storytelling/castleIconData.js for
                          why its icon is inlined rather than fetched.
*/


import { useEffect, useRef, useState } from 'react';

/* The blocking loader and the exit overlay are two separate mounted
   instances, so this has to live at module scope: a ref inside the
   component would be discarded when the blocking one unmounts. Only
   one loader is ever on screen at a time, so a single slot is safe. */
let lastBlockingBox = null;

const SkillLoadingScreen = ({
  iconType = 'logo',
  iconSlug = 'castle',
  iconDir = '/precision-storytelling',
  /* Override the built <iconDir>/<iconSlug>-{day,night}.png path
     entirely — pass a data: URI to inline the icon and avoid a
     separate network request for it. */
  daySrc,
  nightSrc,
  loadingText = 'castle-atlas',
  iconAlt = '',
  /* Set by the useLoadingScreen hook while the loader is fading out. */
  exiting = false,
  /* When true the loader is a fixed overlay ON TOP of the already
     painted page, which is what makes the exit a real crossfade
     instead of a reveal of whatever sits behind it. */
  overlay = false,
}) => {
  /* Reduced motion is handled entirely in CSS (see the
     prefers-reduced-motion block alongside .skill-loading-screen in
     index.css), so there is no motion query in JS here. */
  /* The exit overlay is a NEWLY MOUNTED element: the blocking loader
     unmounts and this one takes its place. A CSS transition needs a
     starting value to animate FROM, and an element that mounts with its
     end state already applied has none — so applying `sls-exiting` on
     the first render made the loader appear at opacity 0 and the
     "crossfade" was a hard cut. Painting once at opacity 1 and arming
     the class on the next frame is what gives the transition something
     to run from. Two rAFs because one only guarantees "before the next
     paint", not "after one has happened". */
  /* ── Geometry, measured for BOTH phases ──────────────────
     The blocking loader is in flow inside <main>, so it starts below
     whatever sits above it (AppLayout's 52px spacer below md, a Navbar
     on public routes). The exit overlay is taken out of flow. If those
     two boxes differ by even a few pixels the centred column visibly
     hops at the handoff, which is the last real source of vertical
     movement on exit.

     So neither phase guesses. Both measure their own distance from the
     top of the viewport and fill exactly the space that remains. That
     is correct whatever is above them, it cannot overflow the viewport
     (which is what used to add a phantom scrollbar on mobile), and it
     makes the two phases geometrically identical by construction. */
  const rootRef = useRef(null);
  const [fill, setFill] = useState(null);
  useEffect(() => {
    const measure = () => {
      const el = rootRef.current;
      if (!el) return;
      /* Blocking measures ITSELF (it is in flow, so its own rect is the
         true content origin). Exiting measures its PARENT, the page
         root, which begins at that same origin — measuring the overlay
         itself would be circular, since a fixed element with no `top`
         falls back to its static position.

         Measuring <main> for both looks tidier and is wrong: AppLayout
         puts its 52px mobile spacer INSIDE <main>, so main's top is
         52px above where either the loader or the page actually starts,
         which reappears as a 26px hop at the handoff on mobile. */
      const base = overlay ? el.parentElement : el;
      if (!base) return;
      const r = base.getBoundingClientRect();
      const next = {
        top: r.top,
        left: r.left,
        width: r.width,
        height: Math.max(0, window.innerHeight - r.top),
      };
      /* The exit overlay reuses the box the blocking phase measured,
         rather than re-deriving it from a different element. Measuring
         the page root instead of the loader leaves a small constant
         offset (~3-4px: the root and the loader do not start on exactly
         the same pixel), and any residual offset is a visible step at
         the handoff no matter how small. Replaying the recorded box
         makes the two phases identical by construction, so the column
         cannot move at all. Falls back to the fresh measurement if the
         loader mounted straight into the exit phase. */
      if (overlay && lastBlockingBox) {
        setFill(lastBlockingBox);
        return;
      }
      if (!overlay) lastBlockingBox = next;
      setFill(next);
    };
    /* One frame late as well as immediately: on the exit the page root
       has only just mounted, and measuring in the same tick can catch
       it before layout has settled. */
    measure();
    const raf = requestAnimationFrame(measure);
    window.addEventListener('resize', measure);
    return () => { cancelAnimationFrame(raf); window.removeEventListener('resize', measure); };
    /* `overlay` never changes for a given mounted instance, but keep it
       in deps so a future call site that toggles it stays correct. */
  }, [overlay]);

  const [armed, setArmed] = useState(false);
  useEffect(() => {
    if (!exiting) { setArmed(false); return undefined; }
    let inner;
    const outer = requestAnimationFrame(() => { inner = requestAnimationFrame(() => setArmed(true)); });
    return () => { cancelAnimationFrame(outer); if (inner) cancelAnimationFrame(inner); };
  }, [exiting]);

  const textColor = 'hsl(var(--muted-foreground))';

  return (
    <div
      ref={rootRef}
      role="status"
      aria-live="polite"
      aria-busy="true"
      className={`skill-loading-screen${overlay ? ' sls-overlay' : ''}${armed ? ' sls-exiting' : ''}`}
      style={{
        ...(fill ? {
          height: fill.height, minHeight: 0,
          ...(overlay ? { top: fill.top, left: fill.left, width: fill.width } : null),
        } : null),
        backgroundColor: 'hsl(var(--background))',
        backgroundImage: 'radial-gradient(circle, hsl(var(--foreground) / 0.05) 1px, transparent 1px)',
        backgroundSize: '22px 22px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        fontFamily: 'var(--font-mono)',
      }}
    >
      <div className="sls-col">
        <div className={`sls-icon${iconType === 'logo' ? ' sls-icon-logo' : ''}`} aria-hidden="true">
          {iconType === 'logo' ? (
            <img src="/logo.svg" alt={iconAlt} className="loading-icon-img sls-logo" />
          ) : (
            <>
              <img
                src={daySrc || `${iconDir}/${iconSlug}-day.png`}
                alt={iconAlt}
                className="loading-icon-day loading-icon-img"
              />
              <img
                src={nightSrc || `${iconDir}/${iconSlug}-night.png`}
                alt=""
                className="loading-icon-night loading-icon-img"
              />
            </>
          )}
        </div>

        <div style={{ fontSize: 11, color: textColor, opacity: 0.8 }}>
          <span style={{ color: 'hsl(var(--primary))' }}>loading</span>{' '}
          {loadingText}
          <span className="sls-caret" aria-hidden="true">_</span>
        </div>
      </div>
    </div>
  );
};

export default SkillLoadingScreen;
